<template>
  <div class="scheme-editor">
    <div
      v-if="errorMessage"
      class="alert alert-danger d-flex align-items-center gap-2 mb-3"
    >
      <i class="bi bi-exclamation-triangle-fill" />
      <span>{{ errorMessage }}</span>
    </div>

    <div class="row g-3 scheme-editor__layout">
      <div :class="canvasColClass">
        <div
          ref="canvasRef"
          class="scheme-editor__canvas"
          :class="{ 'scheme-editor__canvas--panning': isPanning }"
          @mousedown.prevent="startPan"
          @click="handleCanvasClick"
        >
          <div
            ref="frameRef"
            class="scheme-editor__frame"
            :class="{ 'scheme-editor__frame--blank': !schemeImage }"
            :style="frameStyle"
          >
            <div
              class="scheme-editor__zoomable"
              :style="zoomableStyle"
            >
              <img
                v-if="schemeImage"
                :src="schemeImage"
                class="scheme-editor__image"
                alt="Scheme"
                draggable="false"
                @load="onImageLoad"
              >
              <svg
                v-if="hasMetricCalibration"
                class="scheme-editor__grid"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <line
                  v-for="x in gridTicksX"
                  :key="`gx${x}`"
                  :x1="pctX(x)"
                  y1="0"
                  :x2="pctX(x)"
                  y2="100"
                />
                <line
                  v-for="y in gridTicksY"
                  :key="`gy${y}`"
                  x1="0"
                  :y1="pctY(y)"
                  x2="100"
                  :y2="pctY(y)"
                />
              </svg>
              <template v-if="hasMetricCalibration">
                <span
                  v-for="x in gridTicksX"
                  :key="`tx${x}`"
                  class="scheme-editor__tick scheme-editor__tick--x"
                  :style="{ left: pctX(x) + '%' }"
                >{{ x }}</span>
                <span
                  v-for="y in gridTicksY"
                  :key="`ty${y}`"
                  class="scheme-editor__tick scheme-editor__tick--y"
                  :style="{ top: pctY(y) + '%' }"
                >{{ y }}</span>
                <span class="scheme-editor__origin-label">0,0</span>
              </template>
            </div>
            <div
              v-for="(marker, idx) in markersState"
              :key="`${marker.type}-${marker.type === 'anchor' ? marker.address : marker.assetId}-${idx}`"
              class="scheme-marker"
              :class="{ 'scheme-marker--anchor': marker.type === 'anchor' }"
              :style="markerStyle(marker)"
              @mousedown.stop.prevent="startDrag(idx, $event)"
            >
              <div class="scheme-marker__dot" />
              <button
                v-if="!readOnly"
                class="scheme-marker__remove"
                @mousedown.stop.prevent="removeMarker(idx)"
              >
                <i class="bi bi-x" />
              </button>
              <div
                v-if="marker.type === 'anchor'"
                class="scheme-marker__label"
              >
                <div class="scheme-marker__name font-monospace">
                  {{ marker.address }}
                </div>
                <div class="scheme-marker__value">
                  <i class="bi bi-reception-4" /> {{ getAnchorRssi(marker.address) }}
                </div>
              </div>
              <div
                v-else
                class="scheme-marker__label"
              >
                <div class="scheme-marker__name">
                  {{ getAssetLabel(marker.assetId) }}
                </div>
                <div class="scheme-marker__value">
                  <i
                    class="bi bi-thermometer-half"
                    style="color: #e74c3c;"
                  /> {{ getTemperature(marker.assetId) }}
                  <span v-if="getHumidity(marker.assetId) !== null">
                    &nbsp;·&nbsp;<i
                      class="bi bi-droplet-half"
                      style="color: #3498db;"
                    /> {{ getHumidity(marker.assetId) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="scheme-editor__zoom-controls">
            <button
              type="button"
              class="scheme-editor__zoom-btn"
              :title="$t('components.assetSchemes.editor.zoomIn')"
              @mousedown.stop
              @click.stop="zoomBy(1.3)"
            >
              <i class="bi bi-plus-lg" />
            </button>
            <button
              type="button"
              class="scheme-editor__zoom-btn"
              :title="$t('components.assetSchemes.editor.zoomOut')"
              @mousedown.stop
              @click.stop="zoomBy(1 / 1.3)"
            >
              <i class="bi bi-dash-lg" />
            </button>
            <button
              type="button"
              class="scheme-editor__zoom-btn"
              :title="$t('components.assetSchemes.editor.zoomReset')"
              @mousedown.stop
              @click.stop="resetZoom"
            >
              <i class="bi bi-arrow-counterclockwise" />
            </button>
          </div>

          <div class="scheme-editor__readout font-monospace">
            {{ readout || $t('components.assetSchemes.editor.dragHint') }}
          </div>
          <div class="scheme-editor__hint">
            {{ hasMetricCalibration ? $t('components.assetSchemes.editor.originHintMetric') : $t('components.assetSchemes.editor.originHintPercent') }}
          </div>
        </div>
      </div>

      <div
        v-if="!readOnly"
        :class="sideColClass"
      >
        <button
          type="button"
          class="scheme-editor__side-toggle"
          :title="sidebarCollapsed ? $t('components.assetSchemes.editor.expandPanel') : $t('components.assetSchemes.editor.collapsePanel')"
          @click="sidebarCollapsed = !sidebarCollapsed"
        >
          <i
            class="bi"
            :class="sidebarCollapsed ? 'bi-chevron-left' : 'bi-chevron-right'"
          />
        </button>
        <div
          v-show="!sidebarCollapsed"
          class="scheme-editor__side"
        >
          <slot name="side-top" />

          <div class="scheme-panel">
            <div class="scheme-panel__header">
              <i
                class="bi scheme-panel__icon"
                :class="pickerMode === 'asset' ? 'bi-box-seam' : 'bi-broadcast'"
              />
              <span class="scheme-panel__title">{{ pickerMode === 'asset' ? $t('components.assetSchemes.editor.modeAsset') : $t('components.assetSchemes.editor.modeAnchor') }}</span>
            </div>

            <div
              v-if="hasAssets && hasAnchors"
              class="scheme-editor__mode-switch mb-3"
            >
              <button
                type="button"
                class="tab-link"
                :class="{ active: pickerMode === 'asset' }"
                @click="pickerMode = 'asset'"
              >
                {{ $t('components.assetSchemes.editor.modeAsset') }}
              </button>
              <button
                type="button"
                class="tab-link"
                :class="{ active: pickerMode === 'anchor' }"
                @click="pickerMode = 'anchor'"
              >
                {{ $t('components.assetSchemes.editor.modeAnchor') }}
              </button>
            </div>

            <template v-if="pickerMode === 'asset'">
              <div class="asset-select-wrapper position-relative">
                <div class="input-group">
                  <span class="input-group-text">
                    <i class="bi bi-box-seam" />
                  </span>
                  <input
                    v-model="assetSearch"
                    type="text"
                    class="form-control"
                    :placeholder="$t('components.assetSchemes.editor.selectAssetPlaceholder')"
                    autocomplete="off"
                    @focus="showAssetDropdown = true"
                    @input="onAssetSearch"
                    @blur="onAssetBlur"
                  >
                  <button
                    v-if="assetSearch"
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="clearAssetSearch"
                  >
                    <i class="bi bi-x" />
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="toggleAssetDropdown"
                  >
                    <i
                      class="bi"
                      :class="showAssetDropdown ? 'bi-chevron-up' : 'bi-chevron-down'"
                    />
                  </button>
                </div>

                <div
                  v-if="showAssetDropdown"
                  class="asset-dropdown-results"
                >
                  <div
                    v-if="filteredAssets.length === 0"
                    class="dropdown-item text-muted"
                  >
                    <i class="bi bi-info-circle me-2" />
                    {{ $t('components.assetGroups.modal.noAvailableAssets') }}
                  </div>
                  <div
                    v-for="asset in filteredAssets"
                    :key="asset.id"
                    class="dropdown-item asset-dropdown-item"
                    @mousedown.prevent="selectAsset(asset)"
                  >
                    <i class="bi bi-plus-circle me-2 text-success" />
                    <span class="asset-name">{{ asset.name || asset.serialNumber || asset.serial_number }}</span>
                    <small
                      v-if="asset.serialNumber || asset.serial_number"
                      class="text-muted ms-2"
                    >
                      ({{ asset.serialNumber || asset.serial_number }})
                    </small>
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="asset-select-wrapper position-relative">
                <div class="input-group">
                  <span class="input-group-text">
                    <i class="bi bi-broadcast" />
                  </span>
                  <input
                    v-model="anchorSearch"
                    type="text"
                    class="form-control"
                    :placeholder="$t('components.assetSchemes.editor.selectAnchorPlaceholder')"
                    autocomplete="off"
                    @focus="showAnchorDropdown = true"
                    @input="onAnchorSearch"
                    @blur="onAnchorBlur"
                  >
                  <button
                    v-if="anchorSearch"
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="clearAnchorSearch"
                  >
                    <i class="bi bi-x" />
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="toggleAnchorDropdown"
                  >
                    <i
                      class="bi"
                      :class="showAnchorDropdown ? 'bi-chevron-up' : 'bi-chevron-down'"
                    />
                  </button>
                </div>

                <div
                  v-if="showAnchorDropdown"
                  class="asset-dropdown-results"
                >
                  <div
                    v-if="filteredAnchors.length === 0"
                    class="dropdown-item text-muted"
                  >
                    <i class="bi bi-info-circle me-2" />
                    {{ $t('components.assetSchemes.editor.noAvailableAnchors') }}
                  </div>
                  <div
                    v-for="anchor in filteredAnchors"
                    :key="anchor.address"
                    class="dropdown-item asset-dropdown-item"
                    @mousedown.prevent="selectAnchor(anchor)"
                  >
                    <i class="bi bi-plus-circle me-2 text-success" />
                    <span class="asset-name font-monospace">{{ anchor.address }}</span>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div
            v-if="pickerMode === 'anchor' && hasMetricCalibration"
            class="scheme-panel"
          >
            <div class="scheme-panel__header">
              <i class="bi bi-rulers scheme-panel__icon" />
              <span class="scheme-panel__title">{{ $t('components.assetSchemes.editor.calibration.anchorsTableTitle') }}</span>
            </div>
            <div class="scheme-editor__table-wrap">
              <table class="table table-sm scheme-editor__table">
                <thead>
                  <tr>
                    <th>{{ $t('components.assetSchemes.editor.calibration.address') }}</th>
                    <th>X (m)</th>
                    <th>Y (m)</th>
                    <th>Z (m)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="marker in anchorMarkersState"
                    :key="marker.address"
                  >
                    <td class="font-monospace">
                      {{ marker.address }}
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.1"
                        class="form-control form-control-sm"
                        :value="marker.x"
                        @input="updateAnchorCoord(marker.address, 'x', $event)"
                      >
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.1"
                        class="form-control form-control-sm"
                        :value="marker.y"
                        @input="updateAnchorCoord(marker.address, 'y', $event)"
                      >
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.1"
                        class="form-control form-control-sm"
                        :value="marker.z ?? 2.5"
                        @input="updateAnchorCoord(marker.address, 'z', $event)"
                      >
                    </td>
                    <td>
                      <button
                        type="button"
                        class="scheme-editor__row-remove"
                        :title="$t('components.assetSchemes.editor.calibration.remove')"
                        @click="removeMarkerByAddress(marker.address)"
                      >
                        <i class="bi bi-x" />
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!anchorMarkersState.length">
                    <td
                      colspan="5"
                      class="scheme-editor__table-empty"
                    >
                      {{ $t('components.assetSchemes.editor.calibration.noAnchorsPlaced') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="scheme-editor__panel-hint">
              {{ $t('components.assetSchemes.editor.calibration.tableHint') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, onBeforeUnmount, computed, Ref } from 'vue'

type Marker =
  | { type: 'asset'; assetId: number; x: number; y: number }
  | { type: 'anchor'; address: string; x: number; y: number; z?: number }

const MIN_ZOOM = 0.5
const MAX_ZOOM = 4

// Same "nice" step choices as Limonfy's AnchorLayout, so grid density feels the same at any scheme size.
function niceStep(sizeM: number): number {
  const steps = [0.5, 1, 2, 5, 10, 20, 50, 100]
  return steps.find((s) => s >= sizeM / 8) || 100
}

export default defineComponent({
  name: 'SchemeEditor',
  props: {
    schemeImage: { type: String, required: false, default: null },
    markers: { type: Array as () => any[], required: false, default: () => [] },
    assets: { type: Array as () => any[], required: false, default: () => [] },
    anchors: { type: Array as () => any[], required: false, default: () => [] },
    readOnly: { type: Boolean, required: false, default: false },
    initialMode: { type: String as () => 'asset' | 'anchor', required: false, default: 'asset' },
    // Anchor-only: real-world scheme size in meters. Null = legacy percent-only anchors, exactly like asset markers.
    calibration: { type: Object as () => { widthM: number | null; heightM: number | null } | null, required: false, default: null }
  },
  setup(props, { expose }) {
    const canvasRef = ref<HTMLElement | null>(null)
    const frameRef = ref<HTMLElement | null>(null)
    const imageAspect = ref<number | null>(null)
    // markerStyle() reads frameRef's rect imperatively (not tracked by Vue) -- bump this so
    // markers reposition when the frame itself resizes, not just on zoom/pan/marker changes.
    const frameSizeTick = ref(0)
    let frameResizeObserver: ResizeObserver | null = null
    const onImageLoad = (event: Event) => {
      const img = event.target as HTMLImageElement
      if (img.naturalWidth && img.naturalHeight) imageAspect.value = img.naturalWidth / img.naturalHeight
    }
    // The frame is locked to the plan image's true aspect ratio so the canvas can be as tall as the
    // viewport allows without letterboxing throwing off percent-of-image marker positions.
    // No image (Prostokąt mode, or image removed) -- fall back to the calibration's own
    // width/height ratio so the grid still renders as a correctly-proportioned room outline.
    const frameStyle = computed(() => {
      const aspect = imageAspect.value ?? (hasMetricCalibration.value ? (props.calibration!.widthM as number) / (props.calibration!.heightM as number) : null)
      return aspect ? { aspectRatio: String(aspect) } : {}
    })

    // Side panel narrows the canvas by default; collapsing it frees that width back to the plan.
    const sidebarCollapsed = ref(false)
    const canvasColClass = computed(() => {
      if (props.readOnly) return 'col-12'
      return sidebarCollapsed.value ? 'col-lg col-12' : 'col-lg-9 col-12'
    })
    const sideColClass = computed(() => [
      'col-12 scheme-editor__side-col',
      sidebarCollapsed.value ? 'scheme-editor__side-col--collapsed' : 'col-lg-3'
    ])
    const hasAssets = computed(() => props.assets.length > 0)
    const hasAnchors = computed(() => props.anchors.length > 0)
    const pickerMode = ref<'asset' | 'anchor'>(props.initialMode === 'anchor' ? 'anchor' : 'asset')
    // Keep the active tab pointed at a non-empty picker -- e.g. a location with zero assets
    // should land straight on the anchor picker instead of showing an empty, hidden-tab dead end.
    watch([hasAssets, hasAnchors], () => {
      if (pickerMode.value === 'asset' && !hasAssets.value && hasAnchors.value) pickerMode.value = 'anchor'
      else if (pickerMode.value === 'anchor' && !hasAnchors.value && hasAssets.value) pickerMode.value = 'asset'
    }, { immediate: true })
    const selectedAssetId = ref<number | null>(null)
    const assetSearch = ref('')
    const showAssetDropdown = ref(false)
    const selectedAnchorAddress = ref<string | null>(null)
    const anchorSearch = ref('')
    const showAnchorDropdown = ref(false)
    const markersState = ref<Marker[]>([])
    const draggingIndex = ref<number | null>(null)
    const errorMessage = ref<string | null>(null)
    const readout = ref('')

    const hasMetricCalibration = computed(() => !!(props.calibration?.widthM && props.calibration?.heightM))

    const pctX = (xm: number) => (xm / (props.calibration!.widthM as number)) * 100
    const pctY = (ym: number) => (ym / (props.calibration!.heightM as number)) * 100
    const gridTicksX = computed(() => {
      if (!hasMetricCalibration.value) return []
      const widthM = props.calibration!.widthM as number
      const heightM = props.calibration!.heightM as number
      const step = niceStep(Math.max(widthM, heightM))
      const ticks: number[] = []
      for (let x = 0; x <= widthM + 0.001; x += step) ticks.push(Math.round(x * 100) / 100)
      return ticks
    })
    const gridTicksY = computed(() => {
      if (!hasMetricCalibration.value) return []
      const widthM = props.calibration!.widthM as number
      const heightM = props.calibration!.heightM as number
      const step = niceStep(Math.max(widthM, heightM))
      const ticks: number[] = []
      for (let y = 0; y <= heightM + 0.001; y += step) ticks.push(Math.round(y * 100) / 100)
      return ticks
    })

    // Markers loaded from the scheme may predate the 'type' field -- no type + an assetId means
    // 'asset' (backend default, see AssetGroupService::normalizeMarkersArray).
    watch(
      () => props.markers,
      (value) => {
        markersState.value = Array.isArray(value)
          ? value.map((m: any): Marker => (
            m.type === 'anchor'
              ? { type: 'anchor', address: String(m.address), x: Number(m.x), y: Number(m.y), z: m.z !== undefined && m.z !== null ? Number(m.z) : undefined }
              : { type: 'asset', assetId: Number(m.assetId), x: Number(m.x), y: Number(m.y) }
          ))
          : []
      },
      { immediate: true }
    )

    // Switching mode leaves a stale selection behind otherwise -- clear both pickers' state.
    watch(pickerMode, () => {
      assetSearch.value = ''
      selectedAssetId.value = null
      showAssetDropdown.value = false
      anchorSearch.value = ''
      selectedAnchorAddress.value = null
      showAnchorDropdown.value = false
    })

    watch(
      () => selectedAssetId.value,
      (value) => {
        if (!value) {
          assetSearch.value = ''
          return
        }
        const asset = props.assets.find(a => Number(a.id) === Number(value))
        assetSearch.value = asset?.name || asset?.serial_number || asset?.serialNumber || `Asset #${value}`
      }
    )

    const filteredAssets = computed(() => {
      const term = assetSearch.value.toLowerCase().trim()
      if (!term) return props.assets
      return props.assets.filter((asset: any) => {
        const name = (asset.name || '').toLowerCase()
        const serial = (asset.serial_number || asset.serialNumber || '').toLowerCase()
        return name.includes(term) || serial.includes(term)
      })
    })

    // Already-placed anchors drop out of the picker; removing one from the scheme (marker
    // splice, reactive) puts it straight back since this recomputes off markersState.
    const placedAnchorAddresses = computed(() => new Set(
      markersState.value.filter((m): m is Marker & { type: 'anchor' } => m.type === 'anchor').map(m => m.address)
    ))
    const filteredAnchors = computed(() => {
      const term = anchorSearch.value.toLowerCase().trim()
      const available = props.anchors.filter((anchor: any) => !placedAnchorAddresses.value.has(String(anchor.address)))
      if (!term) return available
      return available.filter((anchor: any) => (anchor.address || '').toLowerCase().includes(term))
    })

    const clamp = (value: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, value))

    // Zoom/pan is a pure viewing transform on `.scheme-editor__zoomable`; `frameRef`'s own box
    // never scales and always matches the image's true aspect ratio, so it stays a stable
    // reference frame for converting clicks back to percent-of-image.
    const zoom = ref({ scale: 1, x: 0, y: 0 })
    const zoomableStyle = computed(() => ({
      transform: `translate(${zoom.value.x}px, ${zoom.value.y}px) scale(${zoom.value.scale})`,
      transformOrigin: '0 0'
    }))
    const zoomToward = (cx: number, cy: number, newScale: number) => {
      const k = newScale / zoom.value.scale
      zoom.value = { scale: newScale, x: cx - (cx - zoom.value.x) * k, y: cy - (cy - zoom.value.y) * k }
    }
    const zoomBy = (factor: number) => {
      const rect = frameRef.value?.getBoundingClientRect()
      const cx = rect ? rect.width / 2 : 0
      const cy = rect ? rect.height / 2 : 0
      zoomToward(cx, cy, clamp(zoom.value.scale * factor, MIN_ZOOM, MAX_ZOOM))
    }
    const resetZoom = () => { zoom.value = { scale: 1, x: 0, y: 0 } }
    const onWheel = (event: WheelEvent) => {
      const frame = frameRef.value
      if (!frame) return
      event.preventDefault()
      const rect = frame.getBoundingClientRect()
      const cx = event.clientX - rect.left
      const cy = event.clientY - rect.top
      zoomToward(cx, cy, clamp(zoom.value.scale * Math.exp(-event.deltaY * 0.0015), MIN_ZOOM, MAX_ZOOM))
    }
    onMounted(() => {
      canvasRef.value?.addEventListener('wheel', onWheel, { passive: false })
      if (frameRef.value) {
        frameResizeObserver = new ResizeObserver(() => { frameSizeTick.value++ })
        frameResizeObserver.observe(frameRef.value)
      }
    })
    onBeforeUnmount(() => {
      canvasRef.value?.removeEventListener('wheel', onWheel)
      frameResizeObserver?.disconnect()
    })

    const isPanning = ref(false)
    const panJustMoved = ref(false)
    const panState = ref<{ startX: number; startY: number } | null>(null)
    const onPanMove = (event: MouseEvent) => {
      if (!panState.value) return
      const dx = event.clientX - panState.value.startX - zoom.value.x
      const dy = event.clientY - panState.value.startY - zoom.value.y
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) panJustMoved.value = true
      zoom.value = { ...zoom.value, x: event.clientX - panState.value.startX, y: event.clientY - panState.value.startY }
    }
    const stopPan = () => {
      isPanning.value = false
      panState.value = null
      window.removeEventListener('mousemove', onPanMove)
      window.removeEventListener('mouseup', stopPan)
    }
    const startPan = (event: MouseEvent) => {
      if ((event.target as HTMLElement).closest('.scheme-marker')) return
      isPanning.value = true
      panJustMoved.value = false
      panState.value = { startX: event.clientX - zoom.value.x, startY: event.clientY - zoom.value.y }
      window.addEventListener('mousemove', onPanMove)
      window.addEventListener('mouseup', stopPan)
    }

    const getPositionFromEvent = (event: MouseEvent) => {
      const frame = frameRef.value
      if (!frame) return null
      const rect = frame.getBoundingClientRect()
      const contentX = (event.clientX - rect.left - zoom.value.x) / zoom.value.scale
      const contentY = (event.clientY - rect.top - zoom.value.y) / zoom.value.scale
      const x = (contentX / rect.width) * 100
      const y = (contentY / rect.height) * 100
      return { x: clamp(x), y: clamp(y) }
    }

    const upsertMarker = (predicate: (m: Marker) => boolean, marker: Marker) => {
      const existingIdx = markersState.value.findIndex(predicate)
      if (existingIdx >= 0) markersState.value[existingIdx] = marker
      else markersState.value.push(marker)
    }

    // clamp: legacy percent-valued anchors read as ~400%+ once reinterpreted as meters, pin them
    // to the nearest edge instead of rendering off-canvas.
    // Markers render as a sibling of .scheme-editor__zoomable, not inside it -- a nested
    // counter-scale kept them constant-size but rendered blurry (compounded transforms). Instead
    // this computes their screen position in real px, mirroring getPositionFromEvent's inverse.
    const markerStyle = (marker: Marker) => {
      void frameSizeTick.value // reactive dependency only, see declaration
      const pct = marker.type === 'anchor' && hasMetricCalibration.value
        ? { x: clamp(pctX(marker.x)), y: clamp(pctY(marker.y)) }
        : { x: marker.x, y: marker.y }
      const rect = frameRef.value?.getBoundingClientRect()
      if (!rect) return { left: pct.x + '%', top: pct.y + '%' }
      const left = (pct.x / 100) * rect.width * zoom.value.scale + zoom.value.x
      const top = (pct.y / 100) * rect.height * zoom.value.scale + zoom.value.y
      return { left: `${left}px`, top: `${top}px`, transform: 'translate(-6px, -6px)' }
    }

    const formatReadout = (marker: Marker) => {
      if (marker.type === 'anchor' && hasMetricCalibration.value) {
        return `${marker.address}:  x=${marker.x.toFixed(2)} m   y=${marker.y.toFixed(2)} m`
      }
      const label = marker.type === 'anchor' ? marker.address : getAssetLabel(marker.assetId)
      return `${label}:  x=${marker.x.toFixed(1)}%   y=${marker.y.toFixed(1)}%`
    }

    const DEFAULT_ANCHOR_Z = 2.5

    const handleCanvasClick = (event: MouseEvent) => {
      if (panJustMoved.value) { panJustMoved.value = false; return }
      if (props.readOnly) return
      const pos = getPositionFromEvent(event)
      if (!pos) return

      if (pickerMode.value === 'anchor') {
        if (!selectedAnchorAddress.value) return
        const address = selectedAnchorAddress.value
        if (hasMetricCalibration.value) {
          const widthM = props.calibration!.widthM as number
          const heightM = props.calibration!.heightM as number
          const existing = markersState.value.find(m => m.type === 'anchor' && m.address === address) as (Marker & { type: 'anchor' }) | undefined
          upsertMarker(m => m.type === 'anchor' && m.address === address, {
            type: 'anchor',
            address,
            x: Math.round((pos.x / 100) * widthM * 100) / 100,
            y: Math.round((pos.y / 100) * heightM * 100) / 100,
            z: existing?.z ?? DEFAULT_ANCHOR_Z
          })
        } else {
          upsertMarker(m => m.type === 'anchor' && m.address === address, { type: 'anchor', address, x: pos.x, y: pos.y })
        }
        return
      }

      if (!selectedAssetId.value) return
      const assetId = selectedAssetId.value
      upsertMarker(m => m.type === 'asset' && m.assetId === assetId, { type: 'asset', assetId, x: pos.x, y: pos.y })
    }

    // Asset and anchor pickers are identical apart from which refs they touch and how an
    // item maps to its id/label -- built once here and named individually below since the
    // template binds to each name directly.
    function createPickerHandlers<T>(searchRef: Ref<string>, selectedRef: Ref<any>, showDropdownRef: Ref<boolean>, getId: (item: T) => any, getLabel: (item: T) => string) {
      return {
        onSearch: () => { showDropdownRef.value = true },
        onBlur: () => { setTimeout(() => { showDropdownRef.value = false }, 150) },
        clearSearch: () => { searchRef.value = ''; selectedRef.value = null },
        toggleDropdown: () => { showDropdownRef.value = !showDropdownRef.value },
        select: (item: T) => {
          selectedRef.value = getId(item)
          searchRef.value = getLabel(item)
          showDropdownRef.value = false
        },
      }
    }

    const assetPicker = createPickerHandlers(assetSearch, selectedAssetId, showAssetDropdown,
      (asset: any) => Number(asset.id),
      (asset: any) => asset?.name || asset?.serial_number || asset?.serialNumber || `Asset #${asset.id}`)
    const onAssetSearch = assetPicker.onSearch
    const onAssetBlur = assetPicker.onBlur
    const clearAssetSearch = assetPicker.clearSearch
    const toggleAssetDropdown = assetPicker.toggleDropdown
    const selectAsset = assetPicker.select

    const anchorPicker = createPickerHandlers(anchorSearch, selectedAnchorAddress, showAnchorDropdown,
      (anchor: any) => String(anchor.address),
      (anchor: any) => String(anchor.address))
    const onAnchorSearch = anchorPicker.onSearch
    const onAnchorBlur = anchorPicker.onBlur
    const clearAnchorSearch = anchorPicker.clearSearch
    const toggleAnchorDropdown = anchorPicker.toggleDropdown
    const selectAnchor = anchorPicker.select

    const anchorsByAddress = computed(() => new Map(props.anchors.map((a: any) => [a.address, a])))

    const getAnchorRssi = (address: string) => {
      const value = anchorsByAddress.value.get(address)?.lastRssiDbm
      return value !== null && value !== undefined ? `${value} dBm` : '—'
    }

    const startDrag = (idx: number, event: MouseEvent) => {
      if (props.readOnly) return
      draggingIndex.value = idx
      window.addEventListener('mousemove', handleDrag)
      window.addEventListener('mouseup', stopDrag)
      handleDrag(event)
    }

    const handleDrag = (event: MouseEvent) => {
      if (draggingIndex.value === null) return
      const pos = getPositionFromEvent(event)
      if (!pos) return
      const marker = markersState.value[draggingIndex.value]
      if (!marker) return
      if (marker.type === 'anchor' && hasMetricCalibration.value) {
        const widthM = props.calibration!.widthM as number
        const heightM = props.calibration!.heightM as number
        marker.x = Math.round((pos.x / 100) * widthM * 100) / 100
        marker.y = Math.round((pos.y / 100) * heightM * 100) / 100
      } else {
        marker.x = pos.x
        marker.y = pos.y
      }
      readout.value = formatReadout(marker)
    }

    const removeMarker = (idx: number) => {
      markersState.value.splice(idx, 1)
    }

    const anchorMarkersState = computed(() => markersState.value.filter((m): m is Marker & { type: 'anchor' } => m.type === 'anchor'))

    const removeMarkerByAddress = (address: string) => {
      const idx = markersState.value.findIndex(m => m.type === 'anchor' && m.address === address)
      if (idx >= 0) markersState.value.splice(idx, 1)
    }

    const updateAnchorCoord = (address: string, field: 'x' | 'y' | 'z', event: Event) => {
      const marker = markersState.value.find(m => m.type === 'anchor' && m.address === address) as (Marker & { type: 'anchor' }) | undefined
      if (!marker) return
      const raw = Number((event.target as HTMLInputElement).value)
      if (Number.isNaN(raw)) return
      const maxX = props.calibration?.widthM ?? Infinity
      const maxY = props.calibration?.heightM ?? Infinity
      if (field === 'x') marker.x = Math.max(0, Math.min(maxX, raw))
      else if (field === 'y') marker.y = Math.max(0, Math.min(maxY, raw))
      else marker.z = Math.max(0, Math.min(50, raw))
    }

    const stopDrag = () => {
      draggingIndex.value = null
      window.removeEventListener('mousemove', handleDrag)
      window.removeEventListener('mouseup', stopDrag)
    }

    const getAssetById = (assetId: number) => props.assets.find(a => Number(a.id) === assetId)

    const getAssetLabel = (assetId: number) => {
      const asset = getAssetById(assetId)
      return asset?.name || asset?.serial_number || asset?.serialNumber || `Asset #${assetId}`
    }

    const getParamValue = (asset: any, paramName: string) => {
      if (!asset?.latest_params || !asset.latest_params.length) return null
      const param = asset.latest_params.find((p: any) =>
        p.can_params_io && p.can_params_io.param_name === paramName
      )
      return param?.value !== null && param?.value !== undefined ? param.value : null
    }

    const getTemperature = (assetId: number) => {
      const asset = getAssetById(assetId)
      const value = getParamValue(asset, 'TL_EYE_TEMPERATURE')
      return value !== null ? parseFloat(value).toFixed(1) + '°C' : 'N/A'
    }

    const getHumidity = (assetId: number) => {
      const asset = getAssetById(assetId)
      const value = getParamValue(asset, 'TL_EYE_HUMIDITY')
      return value !== null ? parseFloat(value).toFixed(0) + '%' : null
    }

    expose({
      getMarkers: () => markersState.value,
      getImageAspect: () => imageAspect.value
    })

    onBeforeUnmount(() => {
      stopDrag()
      stopPan()
    })

    return {
      canvasRef,
      frameRef,
      frameStyle,
      onImageLoad,
      sidebarCollapsed,
      canvasColClass,
      sideColClass,
      hasAssets,
      hasAnchors,
      pickerMode,
      selectedAssetId,
      assetSearch,
      showAssetDropdown,
      filteredAssets,
      selectedAnchorAddress,
      anchorSearch,
      showAnchorDropdown,
      filteredAnchors,
      markersState,
      hasMetricCalibration,
      pctX,
      pctY,
      gridTicksX,
      gridTicksY,
      markerStyle,
      anchorMarkersState,
      updateAnchorCoord,
      removeMarkerByAddress,
      errorMessage,
      readout,
      isPanning,
      startPan,
      zoomableStyle,
      zoomBy,
      resetZoom,
      handleCanvasClick,
      onAssetSearch,
      onAssetBlur,
      clearAssetSearch,
      toggleAssetDropdown,
      selectAsset,
      onAnchorSearch,
      onAnchorBlur,
      clearAnchorSearch,
      toggleAnchorDropdown,
      selectAnchor,
      getAnchorRssi,
      startDrag,
      removeMarker,
      getAssetLabel,
      getTemperature,
      getHumidity
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-scheme-editor.scss';
</style>
